<script setup>
const { applicationId } = defineProps({
  applicationId: String,
});

const emits = defineEmits(["onClose"]);

const toast = useToast();
const paymentReceipt = ref();

const uploadFileTypes = ["paymentReceipt"];

function onFileSelect(event, type) {
  if (type === "paymentReceipt") {
    paymentReceipt.value = event.files?.at(0);
  }
}

const handleSubmit = async () => {
  const formData = new FormData();

  formData.append(`receipt`, paymentReceipt.value);

  await useApiFetch(
    `student-applications/${applicationId}/student/payment-upload`,
    {
      method: "POST",
      body: formData,
      onResponse() {
        refreshNuxtData("student-application-detail");
        emits("onClose");
        toast.add({
          severity: "success",
          summary: "Successfully uploaded payment receipt.",
          life: 3000,
        });
      },
    }
  );
};
</script>

<template>
  <div class="px-8 py-5">
    <h2 class="font-semibold text-xl text-primary-500 mb-5">
      Upload Payment Receipt
    </h2>
    <p class="text-sm mb-3">Upload student payment receipt</p>
    <FileUpload
      v-for="uploadFileType in uploadFileTypes"
      :key="uploadFileType"
      customUpload
      @select="(e) => onFileSelect(e, uploadFileType)"
      :maxFileSize="2097152"
      :fileLimit="1"
      accept="image/jpeg, image/png, image/jpg, application/pdf"
      invalidFileSizeMessage="File size should be smaller than 2 MB"
      :pt="{
        header: '!border-0',
        content: '!border-0 !my-2 !w-fit',
      }"
    >
      <template
        #header="{ chooseCallback, clearCallback }"
        class="!border-0 !p-0"
      >
        <Button
          label="Upload Payment Receipt"
          size="small"
          icon="pi pi-upload"
          outlined
          rounded
          @click="chooseCallback()"
        />
      </template>
      <template
        #content="{ files, uploadedFiles, removeFileCallback, messages }"
      >
        <div class="flex flex-col gap-8 py-4 pb-0" v-if="files.length > 0">
          <Message
            v-for="message of messages"
            :key="message"
            class="my-1"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ message }}
          </Message>

          <div class="flex gap-6">
            <div
              v-for="(file, index) of files"
              :key="file.name + file.type + file.size"
              class="inline-flex group items-center gap-4 mb-2 relative group"
            >
              <div
                v-if="file.type === 'application/pdf'"
                class="w-20 h-28 text-center"
              >
                <img
                  src="~/assets/img/pdf.png"
                  :alt="file.name"
                  class="rounded-md object-cover size-20 mx-auto"
                />
                <p class="text-xs text-muted text-ellipsis tracking-tighter">
                  {{ file.name }}
                </p>
              </div>
              <img
                v-else
                role="presentation"
                :alt="file.name"
                :src="file.objectURL"
                class="rounded-md object-cover w-28 h-28"
              />
              <Button
                icon="pi pi-times-circle"
                @click="removeFileCallback(index)"
                rounded
                size="small"
                severity="danger"
                class="!p-0 !-top-2 !absolute !-right-2 !size-5 opacity-0 group-hover:opacity-90"
              />
            </div>
          </div>
        </div>
      </template>
    </FileUpload>
  </div>
  <div class="px-8 py-5 space-x-3 text-right">
    <Button outlined @click="emits('onClose')" size="small">Cancel</Button>
    <Button @click="handleSubmit" size="small" :disabled="!paymentReceipt">
      Send
    </Button>
  </div>
</template>
